import React, { useMemo } from 'react';
import StatusBanner from './StatusBanner';
import PointsProgressBar from './PointsProgressBar';
import SummaryCards from './SummaryCards';
import PackInfoStrip from './PackInfoStrip';
import AccountDistributionChart from './AccountDistributionChart';
import type { FortiFlexReport, FortiFlexAccountLabel } from '../hooks/useFortiFlexData';

interface OverviewTabProps {
    report: FortiFlexReport;
    accountLabels?: FortiFlexAccountLabel[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({ report, accountLabels }) => {
    const rd = report.reportData;
    const summary = rd?.summary;

    const accounts = useMemo(() => {
        const raw = rd?.accounts || [];
        if (!accountLabels?.length) return raw;
        const labelMap = new Map(accountLabels.map((l) => [String(l.accountId), l.label]));
        return raw.map((a) => {
            const live = labelMap.get(String(a.accountId));
            return live && live !== a.label ? { ...a, label: live } : a;
        });
    }, [rd?.accounts, accountLabels]);

    if (!summary) {
        return (
            <p className="text-sm text-semantic-text-secondary py-8 text-center">
                Report data unavailable. Generate a report first.
            </p>
        );
    }

    return (
        <div>
            <StatusBanner
                alertLevel={summary.alertLevel}
                monthsRemaining={summary.monthsRemaining}
                projectedExhaustionDate={summary.projectedExhaustionDate}
            />

            <PointsProgressBar
                pointsUsed={summary.pointsUsed}
                pointsTotal={summary.pointsTotal}
                pctConsumed={summary.pctConsumed}
            />

            <SummaryCards summary={summary} />

            <PackInfoStrip
                packs={rd?.packs || []}
                activeEntitlements={rd?.stats?.activeEntitlements || 0}
                uniqueAccounts={rd?.stats?.uniqueAccountIds?.length || 0}
            />

            <AccountDistributionChart accounts={accounts} />
        </div>
    );
};

export default OverviewTab;
